import React, { useMemo, useState } from "react";
import { useAuth } from "../../common/AuthContext";
import useBrokerOrderWebsocket from "../../../service/websocket/BrokerOrderWebsocket";
import BrokerOrderTable, {
  getOrderStatusCategory,
  ORDER_CATEGORIES,
} from "./BrokerOrderTable";
import "../AlgoTrading.css";
import "./BrokerOrders.css";

const normalizeOrderDate = (value) => {
  if (!value) return "";
  const isoDate = String(value).match(/^\d{4}-\d{2}-\d{2}/)?.[0];
  if (isoDate) return isoDate;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const Orders = () => {
  const { user } = useAuth();
  const { snapshot, orders, isConnected, connectionError } =
    useBrokerOrderWebsocket(user?.accessToken);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedBroker, setSelectedBroker] = useState("");
  const [activeCategory, setActiveCategory] = useState("PENDING");

  const brokers = useMemo(
    () => snapshot.brokers.map((broker) => ({
      id: broker.brokerAccountId,
      name: broker.brokerAccountName || broker.brokerType || broker.brokerAccountId,
    })),
    [snapshot.brokers]
  );
  const ordersForSelectedFilters = useMemo(
    () => orders.filter((order) => {
      if (selectedBroker && order.brokerAccountId !== selectedBroker) return false;
      if (selectedDate && normalizeOrderDate(order.orderTime) !== selectedDate) return false;
      return true;
    }),
    [orders, selectedBroker, selectedDate]
  );
  const categoryCounts = useMemo(
    () => ordersForSelectedFilters.reduce((counts, order) => ({
      ...counts,
      [getOrderStatusCategory(order)]: counts[getOrderStatusCategory(order)] + 1,
    }), { PENDING: 0, EXECUTED: 0, REJECTED: 0, CANCELLED: 0 }),
    [ordersForSelectedFilters]
  );
  const filteredOrders = useMemo(
    () => ordersForSelectedFilters.filter(
      (order) => getOrderStatusCategory(order) === activeCategory
    ),
    [activeCategory, ordersForSelectedFilters]
  );
  const brokerErrors = snapshot.brokers.filter((broker) => !broker.success);

  return (
    <main className="algo-page">
      <header className="algo-page-heading">
        <div><h1>Orders</h1><p>Live order books from your verified broker connections.</p></div>
        <div className={`algo-live-status ${isConnected ? "connected" : ""}`}>
          <span />{isConnected ? "Live · refreshes every 5 seconds" : "Connecting…"}
        </div>
      </header>

      {connectionError && <div className="algo-error">{connectionError}</div>}
      {brokerErrors.map((broker) => (
        <div className="algo-error algo-order-error" key={broker.brokerAccountId}>
          <strong>{broker.brokerAccountName || broker.brokerType}:</strong>{" "}
          {broker.message || "Orders are temporarily unavailable."}
        </div>
      ))}

      <section className="algo-filter-bar">
        <input aria-label="Order date" type="date" value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} />
        <select aria-label="Broker" value={selectedBroker} onChange={(event) => setSelectedBroker(event.target.value)}>
          <option value="">All Brokers</option>
          {brokers.map((broker) => <option key={broker.id} value={broker.id}>{broker.name}</option>)}
        </select>
      </section>

      <section className="algo-content-card">
        <div className="algo-tabs algo-order-tabs" role="tablist" aria-label="Order status">
          {ORDER_CATEGORIES.map((category) => (
            <button
              className={activeCategory === category ? "active" : ""}
              key={category}
              onClick={() => setActiveCategory(category)}
              role="tab"
              aria-selected={activeCategory === category}
            >
              {category[0]}{category.slice(1).toLowerCase()}
              <span>{categoryCounts[category]}</span>
            </button>
          ))}
        </div>
        <div className="algo-order-summary">
          <span>{snapshot.connectedBrokerCount} connected broker(s)</span>
          <span>{filteredOrders.length} {activeCategory.toLowerCase()} / {snapshot.totalOrders} total</span>
          {snapshot.refreshedAt && <span>Updated {new Date(snapshot.refreshedAt).toLocaleTimeString("en-IN")}</span>}
        </div>
        <BrokerOrderTable
          orders={filteredOrders}
          isConnected={isConnected}
          category={activeCategory}
        />
      </section>
    </main>
  );
};

export default Orders;
